import React, { useEffect, useMemo, useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";

import { storageService } from "../../api";
import AuthTokenService from "../auth/AuthTokenService";

import AuthLayout from "../auth/AuthLayout";
import { HeaderActions } from "../../components/HeaderActions";
import { UserProfileModal } from "../../components/ProfileModal/UserProfileModal";
import { ChatHistorySidebar } from "../../components/ChatHistorySidebar";
import { SideNav } from "../../components/SideNav";

import ironworksLogo from "../../assets/logo-ironworks.svg";
import dataMinerIcon from "../../assets/data-miner.svg";

import styles from "./Layout.module.css";

const Layout = () => {
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [hasChats, setHasChats] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isConversationActive, setIsConversationActive] = useState(false);
    const [newChatTrigger, setNewChatTrigger] = useState(0);

    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    const profile = useMemo(() => (isAuthorized ? storageService.getProfile() : null), [isAuthorized]);
    const { isLoggedIn, clearToken } = AuthTokenService(setIsAuthorized);
    useEffect(() => {
        setIsAuthorized(isLoggedIn());
        setIsLoading(false);
    }, [isLoggedIn]);

    return (
        <div className={styles.shell}>
            {profile?.userProfile && <UserProfileModal isProfileOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} profile={profile} />}
            {isAuthorized && (
                <SideNav
                    showProfile={Boolean(profile?.userProfile)}
                    onProfileClick={() => setIsProfileOpen(true)}
                    onLogOutClick={() => {
                        setIsProfileOpen(false);
                        clearToken();
                    }}
                />
            )}
            <div className={`${styles.layout} ${isAuthorized ? styles.layoutWithSideNav : ""}`}>
                <header className={styles.header} role={"banner"}>
                    <div className={styles.headerContainer}>
                        {/* ICE's header lockup: wordmark, then the module's own icon and name. */}
                        <div className={styles.headerTitleContainer}>
                            <Link
                                className={styles.headerBrandLink}
                                to="/"
                                onClick={() => {
                                    setIsConversationActive(false);
                                    setNewChatTrigger(t => t + 1);
                                }}
                            >
                                <img src={ironworksLogo} alt="Ironworks" className={styles.headerWordmark} />
                            </Link>
                            <img src={dataMinerIcon} alt="" className={styles.headerModuleIcon} />
                            <h1 className={styles.headerModuleTitle}>Data Miner</h1>
                        </div>
                        {isAuthorized && (
                            <HeaderActions
                                isChatHistoryOpen={isSidebarOpen}
                                onToggleChatHistory={hasChats ? () => setIsSidebarOpen(open => !open) : undefined}
                            />
                        )}
                    </div>
                </header>
                {!isLoading &&
                    (!isAuthorized ? (
                        <AuthLayout onAuthChange={setIsAuthorized} />
                    ) : (
                        <div className={styles.contentRow}>
                            {/* Dismisses the chat-history sheet; inert from 640px up, where the sheet is a rail. */}
                            <div
                                className={`${styles.sidebarBackdrop} ${isSidebarOpen ? styles.sidebarBackdropOpen : ""}`}
                                onClick={() => setIsSidebarOpen(false)}
                                aria-hidden="true"
                            />
                            {isAuthorized && (
                                <div className={`${styles.sidebarWrapper} ${isSidebarOpen ? styles.sidebarOpen : ""}`}>
                                    <ChatHistorySidebar
                                        onNewChat={() => {
                                            setIsSidebarOpen(false);
                                            setIsConversationActive(false);
                                            setNewChatTrigger(t => t + 1);
                                            navigate("/");
                                        }}
                                        onChatsChange={has => {
                                            setHasChats(has);
                                            if (!has) setIsSidebarOpen(false);
                                        }}
                                    />
                                </div>
                            )}
                            <Outlet context={{ setIsConversationActive, newChatTrigger }} />
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default Layout;
